import yaml from 'js-yaml'
import { LINK_KINDS } from '../types/index.js'

// Only treat a '---' block as frontmatter when it's at the very start of the document
// Allow an optional UTF-8 BOM before the opening dashes. Do NOT use multiline flag.
const FRONTMATTER_RE = /^(?:\uFEFF)?---\s*\n([\s\S]*?)\n---\s*\n?/

// Unicode-friendly slugify: preserves letters/digits from all locales,
// strips diacritics, collapses whitespace/underscores to dashes.
export function slugify(value) {
  const str = String(value || '')
    // Normalize to split base letters and diacritics, then strip diacritics
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[\s_]+/g, '-')
    // Keep any unicode letters/numbers and dashes
    .replace(/[^\p{L}\p{N}-]+/gu, '')
    // Trim leading/trailing dashes
    .replace(/^-+|-+$/g, '')

  // Fallback if slug becomes empty
  return str || 'note'
}

export function extractFrontmatter(markdown) {
  const raw = String(markdown || '')
  const match = raw.match(FRONTMATTER_RE)
  if (!match) {
    return { frontmatter: {}, body: raw }
  }
  const yamlBlock = match[1] || ''
  let frontmatter = {}
  try {
    const parsed = yaml.load(yamlBlock)
    if (parsed && typeof parsed === 'object') frontmatter = parsed
  } catch {
    frontmatter = {}
  }
  const body = raw.replace(FRONTMATTER_RE, '')
  return { frontmatter, body }
}

export function parseInlineTags(markdown) {
  const raw = String(markdown || '')
  const tags = new Set()
  const re = /(^|\s)#([a-zA-Z0-9_-]{2,})/g
  let m
  while ((m = re.exec(raw))) {
    if (!m[2]) continue
    tags.add(m[2])
  }
  return Array.from(tags)
}

export function normalizeTags(frontmatter, markdown) {
  const tags = new Set()
  const fmTags = frontmatter?.tags || frontmatter?.tag || []
  if (Array.isArray(fmTags)) {
    fmTags.forEach((t) => {
      if (typeof t === 'string' && t.trim()) tags.add(t.trim())
    })
  } else if (typeof fmTags === 'string' && fmTags.trim()) {
    fmTags
      .split(/[;,]/)
      .map((t) => t.trim())
      .filter(Boolean)
      .forEach((t) => tags.add(t))
  }

  parseInlineTags(markdown).forEach((t) => tags.add(t))
  return Array.from(tags)
}

export function normalizeAliases(frontmatter) {
  const aliases = new Set()
  const raw = frontmatter?.aliases || frontmatter?.alias || []
  if (Array.isArray(raw)) {
    raw.forEach((a) => {
      if (typeof a === 'string' && a.trim()) aliases.add(a.trim())
    })
  } else if (typeof raw === 'string' && raw.trim()) {
    raw
      .split(/[;,]/)
      .map((a) => a.trim())
      .filter(Boolean)
      .forEach((a) => aliases.add(a))
  }
  return Array.from(aliases)
}

export function parseLinks(markdown) {
  const raw = String(markdown || '')
  const links = []
  const re = /\[\[([^\]]+)\]\]/g
  let match
  while ((match = re.exec(raw))) {
    const inner = String(match[1] || '').trim()
    if (!inner) continue
    // Support multiple pipe segments; for typed entities, up to 3 parts
    const parts = inner.split('|')
    let target = String(parts[0] || '').trim()
    const hasSecond = parts.length >= 2
    const hasThird = parts.length >= 3
    const second = hasSecond ? String(parts[1] || '').trim() : ''
    const third = hasThird ? String(parts[2] || '').trim() : ''
    // For non-typed links, display text is the second part if present
    // For typed entity links, when 3 parts are present: [target, entityLabel, display]
    // When only 2 parts are present: [target, display]

    // External URL inside wikilink (e.g., [[https://...]] or ![[https://...|...]])
    // Do not treat as a note link; mark as URL so other systems ignore creation/edges
    if (/^https?:\/\//i.test(target)) {
      const display = hasSecond ? second : null
      links.push({
        kind: LINK_KINDS.URL,
        targetUrl: target,
        label: display
      })
      continue
    }

    // Typed entity link: entityType:id
    const typedMatch = target.match(/^([a-zA-Z0-9_.-]+):(.+)$/)
    if (typedMatch) {
      const rawType = typedMatch[1]
      const entityType = rawType && rawType.toLowerCase() === 'item' ? 'equipment' : rawType
      const targetId = typedMatch[2]
      if (entityType && targetId) {
        // Determine labels: prefer second as display unless it's a mode keyword
        const isMode = (s) => {
          const v = String(s || '').toLowerCase()
          return v === 'inline' || v === 'compact' || v === 'card' || v === 'avatar'
        }
        const entityLabel = hasSecond && !isMode(second) ? second : null
        let display = hasSecond && !isMode(second) ? second : null
        links.push({
          kind: LINK_KINDS.ENTITY,
          entityType,
          targetId,
          // label remains display label for backward compatibility
          label: display || null,
          entityLabel: entityLabel || null
        })
        continue
      }
    }

    // Note link (support heading subpath: Note#Heading)
    let subpath = ''
    const hashIdx = target.indexOf('#')
    if (hashIdx >= 0) {
      subpath = target.slice(hashIdx + 1).trim()
      target = target.slice(0, hashIdx).trim()
    }
    // Anchor-only link ([[#Heading]]): treat as in-note anchor; do not emit a targetSlug
    if (!target && subpath) {
      links.push({
        kind: LINK_KINDS.NOTE,
        targetSlug: '',
        targetTitle: '',
        label: hasSecond ? second : null,
        subpath
      })
      continue
    }
    const targetSlug = slugify(target)
    links.push({
      kind: LINK_KINDS.NOTE,
      targetSlug,
      targetTitle: target,
      label: hasSecond ? second : null,
      subpath: subpath || null
    })
  }
  return links
}

export function parseMarkdown(markdown) {
  const { frontmatter, body } = extractFrontmatter(markdown)
  const links = parseLinks(body)
  const tags = normalizeTags(frontmatter, body)
  const aliases = normalizeAliases(frontmatter)
  return {
    frontmatter,
    body,
    links,
    tags,
    aliases
  }
}

export function normalizeFrontmatter(frontmatter = {}) {
  const next = { ...frontmatter }
  if (next.permissions) {
    next.permissions = String(next.permissions).toUpperCase()
  }
  return next
}
